// Auth API surface — typed wrappers around the openapi-fetch client.
//
// **Typed against the generated contract** (W46.0). This header used to say
// 「Until `pnpm gen:api` generates `src/types/api.ts`, we keep the shapes
// hand-written in this file … When types land, swap the inline shapes for
// `components.schemas.*`」. Types landed long ago — `src/types/api.ts` is
// ~336 KB of generated contract, and `client.ts` is `createClient<paths>`.
//
// The inline shapes below are still here, and that is a known, tracked gap
// rather than a pending setup step: `User`, `MeResponse` and `AuthResponse`
// each differ from their contract schema (see ADR-0127 §"Known drift"). The
// migration is deliberate and incremental, because aliasing them is a
// behaviour change wherever the two disagree — which is exactly what the
// drift list is for. A new wrapper should alias `components["schemas"][...]`
// directly instead of copying the old pattern.
//
// Per W2 §3.3.2 #2 + #7: openapi-fetch with CSRF middleware handles
// credentials + csrf header.  No manual cookies / headers here.

import { api as openapi } from "./client";
import { assertResponseBody } from "./emptyBody";
import type { RegisterRequest as SchemaRegisterRequest, Session as SchemaSession } from "./schema";
// W39.79. Imported *and* re-exported below: `export … from "./pair"` is a
// re-export, which does not bring the name into this module's scope — so the
// ten call sites further down need a real import as well.
import { ApiError, apiError, type ApiErrorShape } from "./pair";

export { ApiError, apiError };
export type { ApiErrorShape };

export interface User {
  id: string;
  email: string | null;
  username: string | null;
  display_name: string;
  avatar_path: string | null;
  /**
   * Resolved serving path for the avatar, from `GET /api/v1/auth/me`.
   *
   * Added because `avatar_path` is the legacy column and a media-backed avatar
   * never writes it: the upload fills `avatar_media_id` and leaves this null, so
   * every surface reading the auth payload drew the initial letter even after a
   * successful upload — while the profile page, reading `ProfileRead.avatar_url`
   * from the profile module, drew the picture. Same person, same screen, two
   * answers. `PillNav` prefers this and falls back to `avatar_path`.
   */
  avatar_url?: string | null;
  birthday: string | null;
  gender: string | null;
  timezone: string;
  email_verified_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type Session = SchemaSession;

export interface AuthResponse {
  user: User;
  session: Session;
}

export interface MeResponse {
  user: User;
  pair_id: string | null;
  session_expires_at: string | null;
}

// W46.0. The client **is** `createClient<paths>` now (see `client.ts`), so
// `api.GET("/api/v1/auth/me")` is checked against the contract. This cast is
// no longer about the client's typing being absent — it is about the per-module
// `call<T>` funnel taking an untyped `path: string`, which a generated client
// cannot satisfy because `GET` is overloaded per path. The cast is the seam
// where the contract is currently *dropped*, and ADR-0127 tracks removing it
// module by module.
//
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyClient = Record<"GET" | "POST" | "PUT" | "DELETE" | "PATCH", any>;
const api = openapi as unknown as AnyClient;

interface CallOptions {
  body?: unknown;
  headers?: Record<string, string>;
  /** openapi-fetch reads query params from `params.query` — see diary.ts. */
  params?: { query: Record<string, unknown> };
  /**
   * Set `false` only on a route whose OpenAPI contract declares no 2xx body
   * (every one of them answers `204`). A 2xx that arrives empty on any other
   * route is a broken server and must reach `isError` — see `emptyBody.ts`.
   */
  expectBody?: boolean;
}

async function call<T>(
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  path: string,
  opts: CallOptions = {},
): Promise<{ response: Response; data: T | null; error?: unknown }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result = await (api as any)[method](path, opts);
  // openapi-fetch returns { data, error?, response } or { data?, error, response }.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = (result.data as any) ?? null;
  // A bodyless 2xx where the contract declares a body arrives as `null` and
  // every `return res.data as T` below would ship it to a screen. Guard the
  // one funnel rather than each component — see `emptyBody.ts`.
  if (result.response?.ok)
    assertResponseBody(result.response, result.data, opts.expectBody !== false);
  // W39.79. `error` is openapi-fetch's **already-parsed** body for a non-2xx.
  // Without it `apiError` can only try to re-read a body the client consumed.
  return { response: result.response, data, error: (result as { error?: unknown }).error };
}

/** GET /api/v1/auth/me — returns 401 when no session cookie. */
export async function fetchMe(): Promise<MeResponse | null> {
  const res = await call<MeResponse>("GET", "/api/v1/auth/me");
  if (res.response.status === 401) return null;
  if (!res.response.ok) {
    throw new ApiError(res.response.status, "FETCH_ME_FAILED", "無法載入使用者資料");
  }
  return res.data;
}

/** Alias onto the contract — see `schema.ts`. Not a local copy. */
export type RegisterPayload = SchemaRegisterRequest;

/** POST /api/v1/auth/register — creates user + auto-login. */
export async function register(payload: RegisterPayload): Promise<AuthResponse> {
  const res = await call<AuthResponse>("POST", "/api/v1/auth/register", { body: payload });
  if (!res.response.ok) {
    throw await apiError(res, "AUTH_REGISTER_FAILED");
  }
  return res.data as AuthResponse;
}

/** POST /api/v1/auth/login — authenticates and sets cookies. */
export async function login(payload: {
  identifier: string;
  password: string;
}): Promise<AuthResponse> {
  const res = await call<AuthResponse>("POST", "/api/v1/auth/login", { body: payload });
  if (!res.response.ok) {
    throw await apiError(res, "AUTH_LOGIN_FAILED");
  }
  return res.data as AuthResponse;
}

/** POST /api/v1/auth/logout — revokes the session token cookie. */
export async function logout(): Promise<void> {
  const res = await call<unknown>("POST", "/api/v1/auth/logout", { expectBody: false });
  if (!res.response.ok) {
    throw await apiError(res, "AUTH_LOGOUT_FAILED");
  }
}

/** POST /api/v1/auth/forgot-password — always 200, no account enumeration (W2 §4.5). */
export async function forgotPassword(email: string): Promise<{ accepted: boolean }> {
  const res = await call<{ accepted: boolean }>("POST", "/api/v1/auth/forgot-password", {
    body: { email },
  });
  if (!res.response.ok) {
    throw await apiError(res, "AUTH_FORGOT_PASSWORD_FAILED");
  }
  return res.data ?? { accepted: true };
}

/** POST /api/v1/auth/reset-password — consumes token + auto-login (AuthResponse). */
export async function resetPassword(payload: {
  token: string;
  password: string;
}): Promise<AuthResponse> {
  const res = await call<AuthResponse>("POST", "/api/v1/auth/reset-password", { body: payload });
  if (!res.response.ok) {
    throw await apiError(res, "AUTH_RESET_PASSWORD_FAILED");
  }
  return res.data as AuthResponse;
}

/** POST /api/v1/auth/verify-email — mark email_verified_at. 204. */
export async function verifyEmail(token: string): Promise<void> {
  const res = await call<unknown>("POST", "/api/v1/auth/verify-email", {
    expectBody: false,

    body: { token },
  });
  if (!res.response.ok) {
    throw await apiError(res, "AUTH_VERIFY_EMAIL_FAILED");
  }
}

/** POST /api/v1/auth/resend-verification — sends another verify email. */
export async function resendVerification(email: string): Promise<{ accepted: boolean }> {
  const res = await call<{ accepted: boolean }>("POST", "/api/v1/auth/resend-verification", {
    body: { email },
  });
  if (!res.response.ok) {
    throw await apiError(res, "AUTH_RESEND_VERIFICATION_FAILED");
  }
  return res.data ?? { accepted: true };
}

/** POST /api/v1/auth/change-password — CSRF-gated; rotates other sessions. */
export async function changePassword(payload: {
  old_password: string;
  new_password: string;
}): Promise<void> {
  const res = await call<unknown>("POST", "/api/v1/auth/change-password", {
    expectBody: false,

    body: payload,
  });
  if (!res.response.ok) {
    throw await apiError(res, "AUTH_CHANGE_PASSWORD_FAILED");
  }
}

/**
 * The error type, re-exported — not redefined.
 *
 * W39.79. This file used to declare its own `ApiErrorShape`, its own `ApiError`
 * and its own `apiError()`, field for field identical to the ones in
 * `lib/api/pair.ts`, with neither module importing the other. 4 pages imported
 * this copy and ~35 imported that one, and **0 files imported both** — so
 * nothing was broken at the time, which is what made it worth measuring rather
 * than assuming.
 *
 * It was still a live hazard, because these pages branch on
 * `err instanceof ApiError`: a page holding this copy cannot recognise an error
 * built from the other one, and nothing throws when it happens — the page just
 * takes its generic branch. `routes/Account.tsx` hit exactly that while this was
 * being written, and the symptom was indistinguishable from a server bug: every
 * refusal rendered one line reading 「更改密碼失敗」.
 *
 * `pair.ts` owns the definition (35 importers against 4). Those four import paths
 * are left alone so the fix is a re-export rather than a 4-file migration;
 * `scripts/check-api-error-class.mjs` fails if a **second definition** appears, or
 * if any file ends up importing the class from two modules.
 */
